import React from 'react';
import './App.css';

// AWS
import Amplify, {API, Storage} from 'aws-amplify'
import aws_exports from './aws-exports'

// Config
import config from 'react-global-configuration';
import AppConfig from './AppConfig';

// Styles
import { MuiThemeProvider, createTheme } from '@material-ui/core/styles'
import CssBaseline from '@material-ui/core/CssBaseline'

// Routing
import { Router } from '@reach/router'
import ControlTower, {Routes} from "./components/ControlTower";
import NotFoundPage from "./pages/NotFoundPage";
import SignUpPage from "./pages/signup/SignUpPage";
import SignOutPage from "./pages/SignOutPage";
import AccountPage from "./pages/account/AccountPage";
import AccountEditPage from "./pages/account/AccountEditPage";
import ClassPage from "./pages/class/ClassPage";
import LessonPage from "./pages/lesson/LessonPage";
import UserEditPage from "./pages/account/UserEditPage";
import RegisterPage from "./pages/register/RegisterPage";
import AccountClassPage from "./pages/account/AccountClassPage";
import TermsOfServiceDialog from "./components/page/TermsOfServiceDialog";
import PrivacyPolicyDialog from "./components/page/PrivacyPolicyDialog";
import AccountsPage from "./pages/admin/AccountsPage";
import AccountUserPage from "./pages/account/AccountUserPage";
import MyClassesPage from "./pages/myClasses/MyClassesPage";
import SignInPage from "./pages/signIn/SignInPage";
import CoursesPage from "./pages/content/CoursesPage";
import CourseEditPage from "./pages/content/CourseEditPage";
import AccountClassEditPage from "./pages/account/AccountClassEditPage";
import CoursePage from "./pages/course/CoursePage";
import CoursePreviewPage from "./pages/course/CoursePreviewPage";
import ReportsPage from "./pages/reports/ReportsPage";
import PeoplePage from "./pages/admin/PeoplePage";

config.set(AppConfig);

Amplify.configure(aws_exports);
API.configure(aws_exports)
Storage.configure(aws_exports)

// Enable Amplify DEBUG console messages
// window['LOG_LEVEL'] = 'DEBUG';

const theme = createTheme({
  palette: {
    type: "dark",
    "primary": {
      main: 'hsl(6, 4%, 37%)',
    },
    secondary: {
      // Red
      // main: 'hsl(359, 73%, 45%)'
      // Blue
      // main: 'hsla(212, 89%, 35%, 1.00)'
      // Yellow
      main: 'hsla(47, 98%, 52%, 1.00)',
      contrastText: 'rgba(0, 0, 0, 0.87)', // text.primary color
    },
    error: {
      main: 'hsl(359, 93%, 55%)'
    },
    action: {
      active: 'hsl(212, 89%, 65%)',
      // hover: '#000',
      // selected: '#000',
      // disabled: '#000',
      // disabledBackground: '#000',
    }
  },
  overrides: {
    MuiInputLabel: {
      root: {
        color: 'rgba(255,255,255,0.7)',
        "&$focused": { // Make less opaque than default of 0.7
          color: 'rgba(255,255,255,0.6)',
        },
      }
    },
    MuiCardContent: {
      root: {
        padding: 0,
        "&:last-child": {
          paddingBottom: 4,
        },
      },
    },
  },
  typography: {
    fontFamily: [
      'Archivo Black',
      'sans-serif'
    ].join(','),
    h1: {
      fontFamily: [
        'Archivo Black',
        'sans-serif'
      ].join(','),
      fontWeight: 700
    },
    h2: {
      fontFamily: [
        'Archivo Black',
        'sans-serif'
      ].join(','),
      fontWeight: 400
    },
    h3: {
      fontFamily: [
        'Archivo Black',
        'sans-serif'
      ].join(',')
    },
    h4: {
      fontFamily: [
        'Archivo Black',
        'sans-serif'
      ].join(',')
    },
    h5: {
      fontFamily: [
        'Archivo Black',
        'sans-serif'
      ].join(',')
    },
    button: {
      textTransform: 'initial',
      fontFamily: [
        'Archivo',
        'sans-serif'
      ].join(','),
      fontWeight: 400
    },
    body1: {
      fontFamily: [
        'proxima-nova',
        'sans-serif'
      ].join(',')
    },
    body2: {
      fontFamily: [
        'proxima-nova',
        'sans-serif'
      ].join(',')
    },
  }
})

interface IApp {
}

class App extends React.Component<IApp> {
  render() {

    return (
      <MuiThemeProvider theme={theme}>
        <CssBaseline/>
        <Router>
          <ControlTower path={Routes.signin}>
            <SignUpPage path={Routes.signup} />
            <AccountPage path={`${Routes.account}/:accountId`} />
            <AccountPage path={`${Routes.account}/:accountId/:tab`} />
            <AccountClassPage path={`${Routes.account}/:accountId/class/:classId`} />
            <AccountClassEditPage path={`${Routes.account}/:accountId/classEdit/:classId`} />
            <AccountClassEditPage path={`${Routes.account}/:accountId/classEdit`} />
            <AccountEditPage path={`${Routes.accountEdit}/:accountId`}/>
            <AccountEditPage path={`${Routes.accountsEdit}/:accountId`}/>
            <AccountEditPage path={`${Routes.accountsEdit}`}/>
            <AccountsPage path={Routes.accounts} />
            <AccountUserPage path={`${Routes.account}/:accountId/person/:userId`}/>
            <LessonPage path={`${Routes.class}/:classId/lesson/:lessonId`}/>
            <ClassPage path={`${Routes.class}/:classId`}/>
            <CoursePage path={`${Routes.courses}/:courseId`}/>
            <CoursePage path={`${Routes.account}/:accountId/courses/:courseId`}/>
            <CoursePreviewPage path={`${Routes.courses}/:courseId/lesson/:lessonId`}/>
            <CoursePreviewPage path={`${Routes.account}/:accountId/courses/:courseId/lesson/:lessonId`}/>
            <CoursesPage path={Routes.courses} />
            <CourseEditPage path={`${Routes.courseEdit}/:courseId`} />
            <CourseEditPage path={Routes.courseEdit} />
            <MyClassesPage path={Routes.myClasses} />
            <PeoplePage path={Routes.people} />
            <RegisterPage path={`${Routes.register}/:accountId/:userId`} />
            <RegisterPage path={`${Routes.register}/:userId`} />
            <ReportsPage path={`${Routes.reports}`}/>
            <ReportsPage path={`${Routes.reports}/:tab`}/>
            <SignInPage path={Routes.signin}/>
            <SignOutPage path={Routes.signout}/>
            <UserEditPage path={`${Routes.account}/:accountId/userEdit/:userId`} />
            <UserEditPage path={`${Routes.account}/:accountId/userEdit`} />
          </ControlTower>
          <NotFoundPage path={Routes.notFound}/>
        </Router>
        <PrivacyPolicyDialog/>
        <TermsOfServiceDialog/>
      </MuiThemeProvider>
    )

  }
}

export default App;
